import { ChangeDetectionStrategy, Component, computed, inject, NgZone, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  AlertController,
  IonApp,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonSplitPane,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  bugOutline,
  checkmarkCircleOutline,
  chevronForwardOutline,
  clipboardOutline,
  createOutline,
  homeOutline,
  informationCircleOutline,
  logOutOutline,
  mailOutline,
  personOutline,
  personAddOutline,
  readerOutline,
  settingsOutline,
  speedometerOutline,
  warningOutline,
} from 'ionicons/icons';

import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, type PushNotificationSchema } from '@capacitor/push-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { filter, firstValueFrom, take } from 'rxjs';
import { AuthStateService } from './core/services/auth-state.service';
import { AuthService } from './features/auth/services/auth.service';
import { EdgeToEdgeService } from './core/services/edge-to-edge.service';
import { PushTokenService } from './core/services/push-token.service';
import { ADMIN_ROLES, CREATE_USER_ROLES, APP_VERSION, ANDROID_STORE_URL, IOS_STORE_URL } from './core/constants';

interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly route: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonRouterOutlet,
    IonSplitPane,
    IonTitle,
    IonToolbar,
    RouterLink,
    RouterLinkActive,
  ],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authState = inject(AuthStateService);
  private readonly authService = inject(AuthService);
  private readonly edgeToEdge = inject(EdgeToEdgeService);
  private readonly pushToken = inject(PushTokenService);
  private readonly alertCtrl = inject(AlertController);
  private readonly ngZone = inject(NgZone);

  readonly appVersion = signal(APP_VERSION);

  readonly isAuthenticated = this.authState.isAuthenticated;

  /** Roles allowed to create users (can create roles below their own). */
  private static readonly USER_ADMIN_ROLES: readonly string[] = CREATE_USER_ROLES;

  private readonly baseMenuItems: readonly MenuItem[] = [
    { id: 'home', label: 'Home', icon: 'home-outline', route: '/home' },
    { id: 'speed-test', label: 'Internet Speed Testing', icon: 'speedometer-outline', route: '/speed-test' },
    { id: 'profile', label: 'Profile', icon: 'person-outline', route: '/profile' },
    { id: 'reports', label: 'Reports', icon: 'clipboard-outline', route: '/reports' },
    { id: 'support', label: 'Support', icon: 'mail-outline', route: '/support' },
    { id: 'settings', label: 'Settings', icon: 'settings-outline', route: '/settings' },
  ];

  /** Roles allowed to access admin tools (Debug Drafts, Update Answers). */
  private static readonly ADMIN_ROLES: readonly string[] = ADMIN_ROLES;

  /**
   * Menu items, with "Create User" shown for user-admin roles and the admin
   * tools (Debug Drafts, Update Answers) shown only for admin roles.
   */
  readonly menuItems = computed<readonly MenuItem[]>(() => {
    const role = this.authState.userProfile()?.role ?? '';
    const items = [...this.baseMenuItems];
    // Insert after Reports (index 4) so admin actions sit with the other tools.
    let insertAt = 4;
    if (AppComponent.USER_ADMIN_ROLES.includes(role)) {
      items.splice(insertAt++, 0, {
        id: 'create-user',
        label: 'Create User',
        icon: 'person-add-outline',
        route: '/create-user',
      });
    }
    if (AppComponent.ADMIN_ROLES.includes(role)) {
      items.splice(insertAt++, 0,
        {
          id: 'debug-drafts',
          label: 'Debug Drafts',
          icon: 'bug-outline',
          route: '/admin/debug-drafts',
        },
        {
          id: 'update-answers',
          label: 'Update Answers',
          icon: 'create-outline',
          route: '/admin/update-answers',
        },
      );
    }
    return items;
  });

  constructor() {
    addIcons({
      alertCircleOutline,
      bugOutline,
      checkmarkCircleOutline,
      chevronForwardOutline,
      clipboardOutline,
      createOutline,
      homeOutline,
      informationCircleOutline,
      logOutOutline,
      mailOutline,
      personOutline,
      personAddOutline,
      readerOutline,
      settingsOutline,
      speedometerOutline,
      warningOutline,
    });
  }

  private pausedAt = 0;
  private readonly REFRESH_THRESHOLD_MS = 30_000;

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {
      // Dismiss the launch splash now that the app shell has rendered, instead of
      // relying solely on the config's launchShowDuration timeout (kept as a
      // safety net in capacitor.config.json in case this call is ever skipped).
      void SplashScreen.hide();

      void this.checkVersion();

      App.addListener('pause', () => {
        this.pausedAt = Date.now();
      });

      App.addListener('resume', () => {
        const backgroundedFor = Date.now() - this.pausedAt;
        if (backgroundedFor >= this.REFRESH_THRESHOLD_MS) {
          if (this.authState.isAuthenticated()) {
            void this.authService.fetchProfile().catch(() => {});
          }
        }
      });

      if (Capacitor.getPlatform() === 'android') {
        void this.edgeToEdge.enable();
      }
      this.initPush();
    }
  }

  /**
   * GET /mobile/app_version → compare server version to the installed APP_VERSION.
   * If they differ, show an update alert with "Later" and "Update Now".
   * Tapping "Update Now" opens the platform-appropriate store page.
   */
  private async checkVersion(): Promise<void> {
    try {
      const res = await firstValueFrom(this.authService.checkMobileAppVersion());
      if (!res.version || res.version === this.appVersion()) return;

      const storeUrl = Capacitor.getPlatform() === 'ios' ? IOS_STORE_URL : ANDROID_STORE_URL;

      const alert = await this.alertCtrl.create({
        header: `Update Available (v${res.version})`,
        message: res.release_notes,
        backdropDismiss: false,
        buttons: [
          { text: 'Later', role: 'cancel' },
          {
            text: 'Update Now',
            handler: () => {
              void Browser.open({ url: storeUrl });
            },
          },
        ],
      });
      await alert.present();
    } catch {
      // Version check failure must never block the user.
    }
  }

  async logout(): Promise<void> {
    await this.authService.logout();
    await this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  private initPush(): void {
    void PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        void PushNotifications.register();
      }
    });

    PushNotifications.addListener('registration', (token) => {
      this.pushToken.setToken(token.value);
      if (this.authState.isAuthenticated()) {
        void this.authService.registerFcmToken(token.value).catch(() => {});
      }
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      // Runs inside the Capacitor bridge callback, which isn't guaranteed to already be
      // inside Angular's zone — without this the alert can be created but change
      // detection never runs, so it never actually appears on screen.
      this.ngZone.run(() => {
        if (this.authState.isAuthenticated()) {
          void this.authService.fetchProfile().catch(() => {});
        }
        void this.presentPushAlert(notification);
      });
    });

    PushNotifications.addListener('pushNotificationActionPerformed', () => {
      this.ngZone.run(() => {
        if (this.authState.isAuthenticated()) {
          void this.authService.fetchProfile().catch(() => {});
        }
        // authGuard on /notifications handles the redirect-to-login itself if the
        // session hasn't hydrated yet — don't duplicate that check here.
        this.navigateToNotifications();
      });
    });
  }

  /** Foreground push: the OS doesn't show its own banner while the app is open, so show one. */
  private async presentPushAlert(notification: PushNotificationSchema): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: notification.title || 'Notification',
      message: notification.body || '',
      buttons: [
        { text: 'Dismiss', role: 'cancel' },
        { text: 'View', handler: () => this.navigateToNotifications() },
      ],
    });
    await alert.present();
  }

  /**
   * Navigating the instant a notification is tapped from a cold start can race the
   * app's own initial navigation (still resolving app initializers/guards) and get
   * silently superseded once that first navigation completes. Wait for it if needed.
   */
  private navigateToNotifications(): void {
    if (this.router.navigated) {
      void this.router.navigateByUrl('/notifications');
      return;
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => void this.router.navigateByUrl('/notifications'));
  }
}
